class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.recommendationTimer = null;

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();

    /*
      DAVEX RECOMMENDATIONS

      Shopify replaces parts of the cart drawer whenever:
      - an item is added
      - quantity changes
      - an item is removed

      The recommendation HTML gets replaced too, but
      the script inside cart-drawer.liquid does not run again.

      This observer notices when Shopify has redrawn the drawer
      and loads the recommendations again.
    */
    this.recommendationsObserver =
      new MutationObserver(() => {
        this.scheduleRecommendations();
      });

    this.recommendationsObserver.observe(
      this,
      {
        childList: true,
        subtree: true,
      }
    );

    /*
      Also try once when the drawer component first loads.
    */
    setTimeout(() => {
      this.scheduleRecommendations();
    }, 250);
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() === 'SPACE'
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      }
    );
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true,
      }
    );

    /*
      Stop the website behind the drawer from scrolling.
    */
    document.body.classList.add(
      'overflow-hidden'
    );

    /*
      Load recommendations whenever the
      drawer is opened.
    */
    this.scheduleRecommendations();
  }

  close() {
    this.classList.remove('active');

    if (this.activeElement) {
      removeTrapFocus(
        this.activeElement
      );
    } else {
      removeTrapFocus();
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /*
    DAVEX

    Checks whether the recommendation box exists
    and needs loading.

    The data attribute prevents the MutationObserver
    from repeatedly calling the recommendation API
    while the recommendation cards themselves
    are being inserted.
  */
  scheduleRecommendations() {
    clearTimeout(
      this.recommendationTimer
    );

    this.recommendationTimer =
      setTimeout(() => {
        const container =
          this.querySelector(
            '#DavexCartRecommendations'
          );

        if (!container) return;

        /*
          Has this particular recommendation
          container already been requested?
        */
        if (
          container.dataset.davexRequested ===
          'true'
        ) {
          return;
        }

        /*
          Make sure the product ID exists.
        */
        if (
          !container.dataset.productId
        ) {
          return;
        }

        /*
          Make sure our function from
          cart-drawer.liquid exists.
        */
        if (
          typeof window
            .loadDavexCartRecommendations !==
          'function'
        ) {
          return;
        }

        /*
          Mark this specific container as requested
          before calling the loader.

          When Shopify redraws the cart it creates
          a brand-new container, so the new one
          will automatically be allowed to load.
        */
        container.dataset.davexRequested =
          'true';

        window.loadDavexCartRecommendations();
      }, 150);
  }

  renderContents(parsedState) {
    /*
      IMPORTANT:

      If the cart was previously empty,
      Shopify puts "is-empty" on the
      <cart-drawer> element itself.

      Remove it when a product is
      added again.
    */
    this.classList.remove('is-empty');

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (drawerInner) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }

    /*
      cart/add.js may return either a
      single item or multiple items.

      productId is not required for
      rendering, so only set it when
      available.
    */
    if (parsedState.id) {
      this.productId =
        parsedState.id;
    }

    /*
      Make sure Shopify actually
      returned the requested sections.
    */
    if (!parsedState.sections) {
      console.error(
        'Cart drawer could not update because no sections were returned.',
        parsedState
      );

      return;
    }

    /*
      Replace the cart drawer and
      cart icon with Shopify's newly
      rendered HTML.
    */
    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (
          !sectionElement ||
          !parsedState.sections[
            section.id
          ]
        ) {
          return;
        }

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[
              section.id
            ],
            section.selector
          );
      }
    );

    /*
      The overlay was replaced by the
      new HTML, so reconnect its click
      event.
    */
    const overlay =
      this.querySelector(
        '#CartDrawer-Overlay'
      );

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    /*
      Open the newly refreshed drawer.
    */
    setTimeout(() => {
      this.open();

      /*
        The recommendation box now exists
        inside the refreshed drawer.

        Load the recommendations.
      */
      this.scheduleRecommendations();
    }, 0);
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedDocument.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer
);


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);